import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Bell, BellOff, Loader2, Save, Send,
  Check, AlertTriangle, Smartphone, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

// Base64url → Uint8Array for the applicationServerKey on pushManager.subscribe.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

const PUSH_SUPPORTED = typeof window !== "undefined"
  && "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("session_token")}` };
}

export default function Notifications() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  // Push state on THIS device
  const [pushPermission, setPushPermission] = useState(
    PUSH_SUPPORTED ? Notification.permission : "unsupported"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [devices, setDevices] = useState([]);

  useEffect(() => { initialize(); /* eslint-disable-next-line */ }, []);

  async function initialize() {
    try {
      const [prefsRes, devicesRes] = await Promise.all([
        axios.get(`${API}/notifications/prefs`, { headers: authHeaders() }),
        axios.get(`${API}/notifications/push/devices`, { headers: authHeaders() }),
      ]);
      setEvents(prefsRes.data.events || []);
      setDevices(devicesRes.data.devices || []);
      if (PUSH_SUPPORTED) {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setSubscribed(!!sub);
      }
    } catch (err) {
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  }

  function toggleEvent(eventName, channel) {
    setEvents((prev) => prev.map((e) =>
      e.event === eventName ? { ...e, [channel]: !e[channel] } : e
    ));
  }

  async function savePrefs() {
    setSaving(true);
    try {
      const body = {
        events: Object.fromEntries(events.map((e) => [e.event, { sms: !!e.sms, push: !!e.push }])),
      };
      const res = await axios.put(`${API}/notifications/prefs`, body, { headers: authHeaders() });
      setEvents(res.data.events || events);
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function enablePush() {
    if (!PUSH_SUPPORTED) {
      toast.error("This browser doesn't support push notifications");
      return;
    }
    setSubBusy(true);
    try {
      // Register the SW first.
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Ask the user once if they haven't decided.
      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      setPushPermission(permission);
      if (permission !== "granted") {
        toast.error("Notification permission denied — flip it on in your browser settings to enable push.");
        return;
      }

      // Fetch the server's VAPID public key and subscribe.
      const { data } = await axios.get(`${API}/notifications/vapid-public-key`, { headers: authHeaders() });
      const applicationServerKey = urlBase64ToUint8Array(data.key);

      // If an old subscription exists with a stale key, drop it first.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch (_e) {}
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      const subJson = sub.toJSON();
      await axios.post(`${API}/notifications/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
        user_agent: navigator.userAgent,
      }, { headers: authHeaders() });

      setSubscribed(true);
      toast.success("Push notifications enabled on this device 🎉");
      // Refresh device list
      const dRes = await axios.get(`${API}/notifications/push/devices`, { headers: authHeaders() });
      setDevices(dRes.data.devices || []);
    } catch (err) {
      console.error(err);
      toast.error("Could not enable push — " + (err.message || "unknown error"));
    } finally {
      setSubBusy(false);
    }
  }

  async function disablePush() {
    setSubBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await axios.post(`${API}/notifications/push/unsubscribe`,
          { endpoint: sub.endpoint }, { headers: authHeaders() });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push disabled on this device");
      const dRes = await axios.get(`${API}/notifications/push/devices`, { headers: authHeaders() });
      setDevices(dRes.data.devices || []);
    } catch (err) {
      toast.error("Could not disable push");
    } finally {
      setSubBusy(false);
    }
  }

  async function sendTest() {
    setTestBusy(true);
    try {
      const res = await axios.post(`${API}/notifications/push/test`, {}, { headers: authHeaders() });
      const d = res.data || {};
      if (d.sent > 0) {
        toast.success(`Test push sent to ${d.sent} device${d.sent === 1 ? "" : "s"}. Check your phone!`);
      } else if (d.failed > 0) {
        toast.error(`Failed to deliver — ${d.failed} device${d.failed === 1 ? "" : "s"} dropped.`);
      } else {
        toast.error("No devices to send to. Enable push first.");
      }
    } catch (err) {
      toast.error("Test push failed");
    } finally {
      setTestBusy(false);
    }
  }

  async function removeDevice(endpoint) {
    if (!window.confirm("Remove push from this device? It won't receive alerts anymore.")) return;
    try {
      await axios.post(`${API}/notifications/push/unsubscribe`, { endpoint }, { headers: authHeaders() });
      const dRes = await axios.get(`${API}/notifications/push/devices`, { headers: authHeaders() });
      setDevices(dRes.data.devices || []);
      toast.success("Device removed");
      // If we just removed the current device, flip local state.
      if (PUSH_SUPPORTED) {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setSubscribed(!!sub);
      }
    } catch (err) {
      toast.error("Could not remove device");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="mb-6">
          <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
            Notifications
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Pick where each kind of alert goes. Web Push is free; SMS costs Twilio credits.
          </p>
        </div>

        {/* === Push status / enable card === */}
        <div className="bg-white rounded-xl border-2 border-zinc-100 p-5 mb-6">
          <div className="flex items-start gap-3 mb-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${subscribed ? "bg-green-100" : "bg-zinc-100"}`}>
              {subscribed ? <Bell className="w-5 h-5 text-green-600" /> : <BellOff className="w-5 h-5 text-zinc-400" />}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-zinc-900">Push on this device</h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                {!PUSH_SUPPORTED ? (
                  "This browser doesn't support push. Open the portal in Chrome / Safari (iOS 16.4+) and Add to Home Screen."
                ) : subscribed ? (
                  "This device is subscribed. New alerts will pop up natively."
                ) : pushPermission === "denied" ? (
                  "You blocked notification permission. Re-enable it in your browser settings, then come back."
                ) : (
                  "Not subscribed yet. Tap the button below to turn it on."
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {!subscribed ? (
              <button
                onClick={enablePush}
                disabled={subBusy || !PUSH_SUPPORTED || pushPermission === "denied"}
                data-testid="enable-push-btn"
                className="flex items-center gap-1.5 px-4 py-2 bg-[#207024] text-white rounded-lg text-sm font-semibold hover:bg-[#185a1c] disabled:opacity-40"
              >
                {subBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                Enable Push
              </button>
            ) : (
              <button
                onClick={disablePush}
                disabled={subBusy}
                data-testid="disable-push-btn"
                className="flex items-center gap-1.5 px-4 py-2 bg-white border border-zinc-200 text-zinc-700 rounded-lg text-sm font-semibold hover:bg-zinc-50 disabled:opacity-40"
              >
                {subBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellOff className="w-4 h-4" />}
                Disable on this device
              </button>
            )}
            <button
              onClick={sendTest}
              disabled={testBusy || devices.length === 0}
              data-testid="test-push-btn"
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-zinc-200 text-zinc-700 rounded-lg text-sm font-semibold hover:bg-zinc-50 disabled:opacity-40"
            >
              {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send test push
            </button>
          </div>

          {/* iOS hint */}
          {PUSH_SUPPORTED && !subscribed && /iPhone|iPad/.test(navigator.userAgent) && (
            <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <strong>iPhone tip:</strong> Open <span className="font-mono">jobs.screenzz.com</span> in Safari → tap the Share icon → "Add to Home Screen". Push only works from the home-screen app on iOS.
              </div>
            </div>
          )}
        </div>

        {/* === Per-event channel matrix === */}
        <div className="bg-white rounded-xl border-2 border-zinc-100 mb-6">
          <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-3 border-b text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">
            <div>Event</div>
            <div className="text-center w-16">SMS</div>
            <div className="text-center w-16">Push</div>
          </div>
          {events.map((e) => (
            <div key={e.event} className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-4 border-b last:border-b-0 items-center" data-testid={`pref-row-${e.event}`}>
              <div className="text-sm font-medium text-zinc-900">{e.label}</div>
              <ToggleSwitch on={e.sms} onChange={() => toggleEvent(e.event, "sms")} testid={`toggle-${e.event}-sms`} />
              <ToggleSwitch on={e.push} onChange={() => toggleEvent(e.event, "push")} testid={`toggle-${e.event}-push`} />
            </div>
          ))}
        </div>

        <button
          onClick={savePrefs}
          disabled={saving}
          data-testid="save-prefs-btn"
          className="w-full bg-[#207024] text-white py-3 rounded-xl font-semibold hover:bg-[#185a1c] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? "Saving..." : "Save Preferences"}
        </button>

        {/* === Subscribed devices === */}
        {devices.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-zinc-700 mb-3">Subscribed devices</h3>
            <div className="space-y-2">
              {devices.map((d) => (
                <div key={d.device_id} className="bg-white border rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`device-${d.device_id}`}>
                  <Smartphone className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-zinc-500 truncate">{d.user_agent || "Unknown device"}</div>
                    <div className="text-[10px] text-zinc-400">id: {d.device_id}</div>
                  </div>
                  <button
                    onClick={() => removeDevice(d.endpoint)}
                    data-testid={`remove-device-${d.device_id}`}
                    className="p-1.5 hover:bg-red-50 text-red-500 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleSwitch({ on, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={onChange}
      data-testid={testid}
      aria-checked={on}
      role="switch"
      className={`relative w-12 h-6 rounded-full transition-colors ${on ? "bg-[#207024]" : "bg-zinc-300"}`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center transition-transform ${
          on ? "translate-x-6" : "translate-x-0.5"
        }`}
      >
        {on && <Check className="w-3 h-3 text-[#207024]" />}
      </span>
    </button>
  );
}
